<template>
  <div class="report-artifact-actions" :class="{ 'report-artifact-actions--stacked': stacked, 'report-artifact-actions--neutral': neutral }" data-testid="report-artifact-actions">
    <q-btn
      outline
      no-caps
      :color="neutral ? undefined : 'primary'"
      icon="open_in_new"
      label="Открыть PDF"
      :loading="pending === 'open'"
      :disable="disabled || Boolean(pending)"
      data-testid="report-artifact-open"
      @click="openPdf()"
    />
    <q-btn
      :unelevated="!neutral"
      no-caps
      :color="neutral ? undefined : 'primary'"
      icon="download"
      label="Скачать PDF"
      :loading="pending === 'download'"
      :disable="disabled || Boolean(pending)"
      data-testid="report-artifact-download"
      @click="downloadPdf()"
    />
    <q-btn
      v-if="canRetryCurrentForm"
      flat
      no-caps
      :color="neutral ? undefined : 'primary'"
      label="По текущей форме"
      :disable="disabled || Boolean(pending)"
      @click="downloadPdf('current')"
    />
    <div v-if="message || metadata" class="report-artifact-actions__status" role="status">
      <span v-if="message" :class="`report-artifact-actions__message--${status}`">{{ message }}</span>
      <small v-if="metadata">
        {{ metadata.filename }}
        <template v-if="metadata.formRevision"> · редакция {{ metadata.formRevision }}</template>
        <template v-else-if="metadata.formVersion"> · форма {{ metadata.formVersion }}</template>
        · {{ sourceLabel }}
      </small>
      <ul v-if="diagnostics.length">
        <li v-for="(item, index) in diagnostics" :key="index">{{ item.message || item.code }}</li>
      </ul>
    </div>

    <q-dialog v-model="previewOpen" maximized @hide="closePreview">
      <q-card class="report-artifact-preview">
        <q-toolbar>
          <q-toolbar-title>{{ metadata ? metadata.filename : 'PDF отчета' }}</q-toolbar-title>
          <q-btn flat round dense icon="close" aria-label="Закрыть PDF" v-close-popup />
        </q-toolbar>
        <q-separator />
        <object v-if="previewUrl" :data="previewUrl" type="application/pdf" aria-label="PDF отчета с сервера">
          <p>Встроенный просмотр недоступен. Скачайте PDF.</p>
        </object>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import {
  ArtifactRequestError,
  artifactErrorMessage,
  fetchPersistedReportArtifact,
  requestReportArtifact,
  saveReportArtifact,
  triggerBlobDownload
} from '../../services/backendArtifacts'

export default {
  name: 'ReportArtifactActions',
  props: {
    payload: { type: Object, default: null },
    documentId: { type: [String, Number], default: null },
    orgId: { type: [String, Number], required: true },
    historyId: { type: [String, Number], default: 'last' },
    disabled: { type: Boolean, default: false },
    stacked: { type: Boolean, default: false },
    neutral: { type: Boolean, default: false }
  },
  data() {
    return {
      pending: '',
      status: 'idle',
      message: '',
      metadata: null,
      diagnostics: [],
      lastErrorCode: '',
      controller: null,
      previewUrl: '',
      previewOpen: false
    }
  },
  computed: {
    isPersisted() {
      return this.documentId !== null && this.documentId !== undefined && this.documentId !== ''
    },
    isHistorical() {
      return this.historyId !== null && this.historyId !== undefined && this.historyId !== '' && this.historyId !== 'last'
    },
    sourceLabel() {
      if (this.isHistorical) {
        return this.metadata && this.metadata.selectionMode === 'current'
          ? `история №${this.historyId}, текущая редакция формы`
          : `история №${this.historyId}`
      }
      return this.isPersisted ? 'сохраненный отчет' : 'параметры сформированного отчета'
    },
    canRetryCurrentForm() {
      return this.isHistorical && this.lastErrorCode === 'form_version_not_found'
    },
    suggestedName() {
      const code = this.payload && this.payload.report_code || 'report'
      const date = this.payload && (this.payload.period_end || this.payload.date) || ''
      return `${code}-${String(date).slice(0, 10) || 'current'}.pdf`
    }
  },
  watch: {
    payload: { deep: true, handler: 'reset' },
    documentId: 'reset',
    historyId: 'reset'
  },
  beforeUnmount() {
    this.abort()
    this.closePreview()
  },
  methods: {
    normalizedFormVersion(value) {
      return value === 'current' ? 'current' : undefined
    },
    artifactPayload(formVersion) {
      const normalized = this.normalizedFormVersion(formVersion)
      const payload = { ...this.payload, org_id: this.orgId }
      if (normalized) payload.form_version = normalized
      else delete payload.form_version
      return payload
    },
    abort() {
      this.controller?.abort()
      this.controller = null
    },
    begin(intent) {
      this.abort()
      this.controller = new AbortController()
      this.pending = intent
      this.status = 'pending'
      this.message = intent === 'download' ? 'Готовим PDF к скачиванию…' : 'Получаем PDF с сервера…'
      this.diagnostics = []
      this.lastErrorCode = ''
      return this.controller.signal
    },
    async request(formVersion, signal) {
      const normalizedFormVersion = this.normalizedFormVersion(formVersion)
      if (this.isPersisted) {
        return fetchPersistedReportArtifact({
          connection: this.connection,
          documentId: this.documentId,
          orgId: this.orgId,
          historyId: this.historyId,
          formVersion: normalizedFormVersion,
          signal
        })
      }
      if (!this.payload) throw new ArtifactRequestError({ code: 'validation_error', status: 422 })
      return requestReportArtifact({
        connection: this.connection,
        payload: this.artifactPayload(normalizedFormVersion),
        signal
      })
    },
    succeed(result, message) {
      this.pending = ''
      this.status = 'success'
      this.message = message
      this.metadata = result.metadata || null
      this.diagnostics = []
      this.controller = null
    },
    fail(error) {
      if (error && error.name === 'AbortError') {
        this.pending = ''
        return
      }
      this.pending = ''
      this.status = 'error'
      this.message = artifactErrorMessage(error)
      this.diagnostics = Array.isArray(error && error.diagnostics) ? error.diagnostics : []
      this.lastErrorCode = error && error.code || ''
      this.controller = null
    },
    async openPdf(formVersion) {
      if (this.disabled || this.pending) return
      const signal = this.begin('open')
      try {
        const result = await this.request(formVersion, signal)
        if (result.delivery === 'one_time_ticket') {
          throw new ArtifactRequestError({
            code: 'artifact_size_limit',
            message: 'Отчет слишком большой для предпросмотра. Используйте «Скачать PDF».'
          })
        }
        this.closePreview()
        this.previewUrl = URL.createObjectURL(result.blob)
        this.previewOpen = true
        this.succeed(result, 'PDF готов к просмотру.')
      } catch (error) {
        this.fail(error)
      }
    },
    async downloadPdf(formVersion) {
      if (this.disabled || this.pending) return
      const signal = this.begin('download')
      try {
        let result
        if (this.isPersisted) {
          result = await this.request(formVersion, signal)
          triggerBlobDownload(result.blob, result.metadata.filename)
        } else {
          result = await saveReportArtifact({
            connection: this.connection,
            payload: this.artifactPayload(formVersion),
            suggestedName: this.suggestedName,
            signal
          })
        }
        this.succeed(result, `Файл ${result.metadata.filename} сохранен.`)
      } catch (error) {
        this.fail(error)
      }
    },
    closePreview() {
      this.previewOpen = false
      if (this.previewUrl) URL.revokeObjectURL(this.previewUrl)
      this.previewUrl = ''
    },
    reset() {
      this.abort()
      this.closePreview()
      this.pending = ''
      this.status = 'idle'
      this.message = ''
      this.metadata = null
      this.diagnostics = []
      this.lastErrorCode = ''
    }
  }
}
</script>

<style scoped>
.report-artifact-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: var(--sid-space-2); }
.report-artifact-actions--stacked { justify-content: flex-start; }
.report-artifact-actions--neutral :deep(.q-btn) { color: var(--sid-color-text-primary); }
.report-artifact-actions__status { flex: 1 1 100%; display: grid; gap: var(--sid-space-1); min-width: 0; color: var(--sid-color-text-secondary); font-size: var(--sid-font-size-label); }
.report-artifact-actions__status small { overflow-wrap: anywhere; }
.report-artifact-actions__status ul { margin: 0; padding-left: var(--sid-space-5); color: var(--sid-color-danger); }
.report-artifact-actions__message--error { color: var(--sid-color-danger); }
.report-artifact-actions__message--success { color: var(--sid-color-success); }
.report-artifact-preview { display: grid; grid-template-rows: auto 1px minmax(0, 1fr); background: var(--sid-color-surface-1); }
.report-artifact-preview object { width: 100%; height: 100%; min-height: 0; border: 0; }
@media (max-width: 599px) {
  .report-artifact-actions { justify-content: stretch; }
  .report-artifact-actions :deep(.q-btn) { flex: 1 1 auto; min-height: 44px; }
}
</style>
